import React from 'react';
import { z } from 'zod';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { td } from '@/lib/i18n';
import { toast } from 'sonner';
import { CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { FormControl, FormField, FormItem, FormLabel, FormMessage, Form } from '@/components/ui/form';
import { TextField } from '@/components/text-field';
import { TextAreaField } from '@/components/textarea-field';
import { SelectField } from '@/components/select-field';
import { DateField } from '@/components/date-field';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue, } from '@/components/ui/select';
import { campaignAssignmentSchema } from '@/schema/campaign-assignment-schema';
import { fetchCampaignAssignments, postCampaignAssignment } from '@/services/campaign-assignment';
import { fetchDrivers } from '@/services/driver';
import { fetchInternalCampaigns } from '@/services/internal-campaign';
import { errorHandler } from '@/lib/utils';
function CampaignRow({ campaign }) {
    return (<div className="flex items-center gap-2 min-w-0">
            {campaign.imageThumbnailUrl ? (<img src={campaign.imageThumbnailUrl} alt="" className="h-8 w-8 rounded object-cover border shrink-0" loading="lazy"/>) : (<div className="h-8 w-8 rounded border bg-muted shrink-0"/>)}
            <span className="truncate">{campaign.name}</span>
        </div>);
}
export const Route = createFileRoute('/console/_authenticated/_layout/internal/_layout/assignments/new')({
    component: NewAssignmentPage,
    // Pre-fill campaign / driver when arriving from existing detail pages.
    validateSearch: z.object({
        campaignPublicId: z.string().optional(),
        driverPublicId: z.string().optional(),
    }),
});
function NewAssignmentPage() {
    const { t } = useTranslation('internal');
    const navigate = Route.useNavigate();
    const { campaignPublicId: prefilledCampaign, driverPublicId: prefilledDriver } = Route.useSearch();
    const { data: drivers = [] } = useQuery({ queryKey: ['drivers'], queryFn: fetchDrivers });
    const { data: campaigns = [] } = useQuery({
        queryKey: ['internal-campaigns'],
        queryFn: fetchInternalCampaigns,
    });
    const { data: assignments = [] } = useQuery({
        queryKey: ['campaign-assignments'],
        queryFn: fetchCampaignAssignments,
    });
    const driversWithOpenAssignment = React.useMemo(() => new Set(assignments
        .filter((a) => a.status === 'scheduled' || a.status === 'active')
        .map((a) => a.driverPublicId)), [assignments]);
    const form = useForm({
        resolver: yupResolver(campaignAssignmentSchema),
        defaultValues: {
            driverPublicIds: prefilledDriver ? [prefilledDriver] : [],
            campaignPublicId: prefilledCampaign ?? '',
            startDate: new Date().toISOString().slice(0, 10),
            endDate: '',
            monthlyRate: 0,
            currency: 'USD',
            photoRequestCycle: 'monthly',
            rateCycle: 'monthly',
            notes: '',
        },
    });
    const selectedDriverIds = form.watch('driverPublicIds') ?? [];
    // "Copy from existing assignment" — picking a row clones its campaign,
    // rate, currency, and cycles into the form so staff don't retype when
    // assigning a new driver to a campaign someone is already running. The
    // Select itself remounts via a key after each pick so the trigger always
    // shows the placeholder again — there's no "selected source" state to
    // remove later, the form fields are what got copied.
    const [copySourceKey, setCopySourceKey] = React.useState(0);
    const copyFromAssignment = (publicId) => {
        const source = assignments.find((a) => a.publicId === publicId);
        if (!source)
            return;
        form.setValue('campaignPublicId', source.campaignPublicId);
        form.setValue('monthlyRate', parseFloat(source.monthlyRate));
        form.setValue('currency', source.currency);
        form.setValue('photoRequestCycle', source.photoRequestCycle);
        // Defensive default for assignments created before rateCycle shipped
        // (backend always returns a value now, but the API contract is recent).
        form.setValue('rateCycle', source.rateCycle ?? 'monthly');
        // Force-remount the Select so its internal value clears.
        setCopySourceKey((k) => k + 1);
    };
    const { mutate, isPending } = useMutation({
        mutationFn: postCampaignAssignment,
        onSuccess: (created) => {
            toast.success(created.length > 1
                ? t('assignments.toast.bulk_created', {
                    count: created.length,
                    defaultValue: '{{count}} assignments created',
                })
                : t('assignments.toast.created'));
            navigate({ to: '/console/internal/assignments' });
        },
        onError: errorHandler,
    });
    // Group campaigns by their owning internal workspace so the dropdown
    // splits neatly when several internal workspaces own similarly-named
    // campaigns. Insertion order = order returned by /internal/campaigns
    // (workspace name ASC, campaign name ASC).
    const campaignsByWorkspace = React.useMemo(() => {
        const map = new Map();
        for (const c of campaigns) {
            const group = map.get(c.workspacePublicId);
            if (group) {
                group.items.push(c);
            }
            else {
                map.set(c.workspacePublicId, { name: c.workspaceName, items: [c] });
            }
        }
        return Array.from(map.values());
    }, [campaigns]);
    const cycleOptions = ['weekly', 'bi_weekly', 'monthly'].map((value) => ({
        value,
        label: td(t, `enum.photo_cycle.${value}`),
    }));
    const rateCycleOptions = ['weekly', 'bi_weekly', 'monthly'].map((value) => ({
        value,
        label: td(t, `enum.rate_cycle.${value}`),
    }));
    const currencyOptions = [
        { value: 'USD', label: 'USD' },
        { value: 'KHR', label: 'KHR' },
        { value: 'EUR', label: 'EUR' },
    ];
    return (<div className="p-6 space-y-4 w-full">
            <div className="space-y-1 border-b pb-4">
                <h1 className="text-3xl font-semibold">{t('assignments.page.new_title')}</h1>
                <p className="text-sm text-muted-foreground">{t('assignments.page.new_desc')}</p>
            </div>

            <Form {...form}>
                <Card>
                    <CardHeader>
                        <CardTitle>{t('assignments.section.pair')}</CardTitle>
                        <CardDescription>
                            {t('assignments.section.pair_desc_bulk', {
            defaultValue: 'Pick one or more drivers — one assignment is created per driver, all with the same campaign and rate.',
        })}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <FormField control={form.control} name="driverPublicIds" render={({ field }) => (<FormItem>
                                    <div className="flex items-center justify-between">
                                        <FormLabel>
                                            {t('assignments.field.drivers', { defaultValue: 'Drivers' })}
                                        </FormLabel>
                                        <span className="text-xs text-muted-foreground">
                                            {t('assignments.field.drivers_selected', {
                count: selectedDriverIds.length,
                defaultValue: '{{count}} selected',
            })}
                                        </span>
                                    </div>
                                    <FormControl>
                                        <div className="rounded-md border max-h-64 overflow-y-auto divide-y">
                                            {drivers.length === 0 ? (<p className="p-3 text-sm text-muted-foreground">
                                                    {t('assignments.field.drivers_empty', {
                    defaultValue: 'No drivers available.',
                })}
                                                </p>) : (drivers.map((driver) => {
                const taken = driversWithOpenAssignment.has(driver.publicId);
                const checked = (field.value ?? []).includes(driver.publicId);
                return (<label key={driver.publicId} className={'flex items-center justify-between gap-3 px-3 py-2.5 text-sm transition-colors ' +
                        (taken
                            ? 'opacity-50 cursor-not-allowed'
                            : 'hover:bg-muted/40 cursor-pointer')}>
                                                            <div className="flex items-center gap-3 min-w-0">
                                                                <Checkbox checked={checked} disabled={taken || isPending} onCheckedChange={(next) => {
                        const current = field.value ?? [];
                        if (next) {
                            if (!current.includes(driver.publicId)) {
                                field.onChange([...current, driver.publicId]);
                            }
                        }
                        else {
                            field.onChange(current.filter((id) => id !== driver.publicId));
                        }
                    }}/>
                                                                {driver.vehicleImageUrl ? (<img src={driver.vehicleImageUrl} alt="" className="h-10 w-10 rounded object-cover border shrink-0" loading="lazy"/>) : (<div className="h-10 w-10 rounded border bg-muted text-xs flex items-center justify-center text-muted-foreground shrink-0">
                                                                        {driver.fullName.slice(0, 2).toUpperCase()}
                                                                    </div>)}
                                                                <div className="min-w-0">
                                                                    <p className="font-medium truncate">{driver.fullName}</p>
                                                                    <p className="text-xs text-muted-foreground truncate">
                                                                        {driver.licensePlate}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            {taken && (<Badge variant="outline" className="shrink-0">
                                                                    {t('assignments.field.driver_taken_suffix')}
                                                                </Badge>)}
                                                        </label>);
            }))}
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>)}/>
                        <FormField control={form.control} name="campaignPublicId" render={({ field }) => {
            // Trigger needs to re-show campaign row (image + name) once a
            // value is picked. Lookup lives next to the field so it auto-
            // recomputes whenever react-hook-form pushes a new value.
            const selected = campaigns.find((c) => c.publicId === field.value);
            return (<FormItem>
                                        <FormLabel>{t('assignments.field.campaign')}</FormLabel>
                                        <Select value={field.value || undefined} onValueChange={field.onChange} disabled={isPending}>
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder={t('assignments.field.campaign')}>
                                                        {selected && <CampaignRow campaign={selected}/>}
                                                    </SelectValue>
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {campaignsByWorkspace.map((group) => (<SelectGroup key={group.name}>
                                                        <SelectLabel>{group.name}</SelectLabel>
                                                        {group.items.map((c) => (<SelectItem key={c.publicId} value={c.publicId}>
                                                                <CampaignRow campaign={c}/>
                                                            </SelectItem>))}
                                                    </SelectGroup>))}
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>);
        }}/>

                        {assignments.length > 0 && (<div className="space-y-2 rounded-md border border-dashed p-3">
                                <Label className="text-sm">{t('assignments.field.copy_from_existing')}</Label>
                                <p className="text-xs text-muted-foreground">
                                    {t('assignments.field.copy_from_existing_desc')}
                                </p>
                                <Select key={copySourceKey} onValueChange={copyFromAssignment} disabled={isPending}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={t('assignments.field.copy_from_existing_placeholder')}/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {assignments.map((a) => (<SelectItem key={a.publicId} value={a.publicId}>
                                                {a.driverName} · {a.campaignName} · {a.currency} {a.monthlyRate}
                                            </SelectItem>))}
                                    </SelectContent>
                                </Select>
                            </div>)}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{t('assignments.section.schedule')}</CardTitle>
                        <CardDescription>{t('assignments.section.schedule_desc')}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid gap-4 md:grid-cols-2">
                            <DateField control={form.control} name="startDate" label={t('assignments.field.start_date')} disabled={isPending}/>
                            <DateField control={form.control} name="endDate" label={t('assignments.field.end_date_optional')} disabled={isPending} nullable/>
                        </div>
                        <SelectField control={form.control} name="photoRequestCycle" label={t('assignments.field.photo_cycle')} options={cycleOptions} disabled={isPending}/>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{t('assignments.section.rate')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid gap-4 md:grid-cols-3">
                            <TextField control={form.control} name="monthlyRate" type="number" step="0.01" label={t('assignments.field.monthly_rate')} disabled={isPending}/>
                            <SelectField control={form.control} name="rateCycle" label={t('assignments.field.rate_cycle')} options={rateCycleOptions} disabled={isPending}/>
                            <SelectField control={form.control} name="currency" label={t('assignments.field.currency')} options={currencyOptions} disabled={isPending}/>
                        </div>
                        <TextAreaField control={form.control} name="notes" label={t('assignments.field.notes')} disabled={isPending} className="mt-4"/>
                    </CardContent>
                </Card>

                <div className="pt-4">
                    <Button size="lg" className="w-full" onClick={form.handleSubmit((data) => mutate(data))} disabled={isPending}>
                        <CheckCircle className="h-4 w-4"/>
                        {t('assignments.action.create')}
                    </Button>
                </div>
            </Form>
        </div>);
}
